import { useLocation, useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { CUSTOM_FILTERS_SEARCH_PARAM } from 'app/components/analytics/DashboardHeader'
import { CallsModal } from 'app/components/scorecards/CallsModal'
import { decodeFilters, FilterOperator, PredefinedFilter } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import useLocationMetrics from 'app/hooks/useLocationMetrics'
import useStaffMetrics from 'app/hooks/useStaffMetrics'
import { closeModal, showModal } from 'app/lib/modal'
import { defaultLocationColumns, callTypeTitle, getScorecardStaffColumns } from 'app/table-columns/analytics/marketing'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { gqlQuery } from 'app/lib/graphqlClient'
import { debug } from 'lib/logging'
import type { StaffBreakdownRow as StaffRow } from 'lib/staff-breakdown'
import { defaultNonPatientFilter, defaultBecameLeadDateFilter } from 'app/components/analytics-marketing/defaultFilters'
import { graphql } from 'app/generated/gql'

const MARKETING_LOCATIONS_QUERY = graphql(`
  query MarketingLocations {
    locations {
      hits {
        id
        name
        tags
        orgTags
        pmsIntegrated
        lastPmsSyncDate
        workspaceId
      }
    }
  }
`)

const STAFF_BREAKDOWN_QUERY = graphql(`
  query MarketingStaffBreakdown(
    $dateRange: DateRangeInput!
    $channel: String
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
  ) {
    staffBreakdown(
      dateRange: $dateRange
      channel: $channel
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
    ) {
      hits {
        bucket
        id
        date
        isStaff
        newPatientCalls
        qualifiedNPCalls
        disqualifiedNPCalls
        scheduledNPCalls
        unscheduledNPCalls
        averageDuration
        speedToLead
        NPCallScheduleRate
      }
    }
  }
`)

export type UseMarketingReturn = {
  user: ReturnType<typeof useUser>
  channel: string
  filters: string | undefined
  fromDate: string
  toDate: string
  compareFromDate: string
  compareToDate: string
  isComparingToGoals: boolean
  dontCompare: boolean
  platform: string | null
  showDisqualified: boolean
  openTab: string
  canViewStaffBreakdown: boolean
  canViewMarketingSpend: boolean
  summary: any
  compareTo: any
  locationRows: any
  isLocationsLoading: boolean
  staffRows: any
  isStaffLoading: boolean
  scorecardStaffRows: StaffRow[] | undefined
  isScorecardStaffLoading: boolean
  locationColumns: any
  staffColumns: any
  locationIds: string[]
  integrationStatus: string
  defaultFilters: PredefinedFilter
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  handleTabChange: (tab: string) => void
  handleShowDisqualifiedToggle: (value: boolean) => void
  setFilters: (filters: string | undefined) => void
  setLocationSearchQuery: (query: string) => void
  setStaffSearchQuery: (query: string) => void
}

/** Opens the CallsModal for a staff/call-type scorecard drill-down. Both the
 * deep-link effect and the scorecard column handler in useMarketing open the
 * same modal with the same options, so the call lives here once. */
function openCallsModal(args: {
  staff: string | null
  staffId: string | null
  callType: string
  fromDate: string
  toDate: string
  channel?: string
  locationIds?: string[]
  filters?: string
}) {
  const { staff, staffId, callType, fromDate, toDate, channel, locationIds, filters } = args
  const isMatched = staffId && staffId !== staff
  showModal(
    <CallsModal
      title={callTypeTitle(callType)}
      subtitle={staff ?? undefined}
      {...(isMatched ? { staffId: staffId ?? undefined } : { staffName: staff ?? undefined })}
      type={callType as any}
      fromDate={fromDate}
      toDate={toDate}
      channel={channel}
      locationIds={locationIds}
      filters={filters}
      onClose={() => closeModal()}
    />,
    {
      hideTitle: true,
      removePadding: true,
      isFullScreen: true,
      useRadix: true,
    },
  )
}

export function useMarketing(): UseMarketingReturn {
  const user = useUser()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const checkPermission = useCheckPermission()
  const isOrgUser = useIsOrgUser()

  const canViewMarketingSpend = checkPermission([`org:marketing:see-spend`, `marketing:spend`])
  const canViewOwnPerformance = checkPermission(`staff:performance:own`)
  const canViewOthersPerformance = checkPermission(`staff:performance:others`)
  const canViewStaffBreakdown = isOrgUser || canViewOwnPerformance || canViewOthersPerformance

  const [locationSearchQuery, setLocationSearchQuery] = useState<string>(``)
  const [staffSearchQuery, setStaffSearchQuery] = useState<string>(``)
  const [channel, setChannel] = useState<string>(searchParams.get(`channel`) ?? ``)
  const channelOp = searchParams.get(`channelOp`)
  const [filters, setFilters] = useState<string | undefined>(searchParams.get(CUSTOM_FILTERS_SEARCH_PARAM) || ``)
  const [openTab, setOpenTab] = useState<string>(searchParams.get(`tab`) ?? `Summary`)
  const locationIdsMemoed = useMemo(() => searchParams.get(`locationIds`), [searchParams])
  const locationIds = useMemo(() => compact(locationIdsMemoed?.split(`,`) ?? []), [locationIdsMemoed])
  const integrationStatus = useMemo(() => searchParams.get(`integrationStatus`) ?? ``, [searchParams])
  const [fromDate, setFromDate] = useState<string>(searchParams.get(`fromDate`) ?? ``)
  const [toDate, setToDate] = useState<string>(searchParams.get(`toDate`) ?? ``)
  const [compareFromDate, setCompareFromDate] = useState<string>(searchParams.get(`compareFromDate`) ?? ``)
  const [compareToDate, setCompareToDate] = useState<string>(searchParams.get(`compareToDate`) ?? ``)
  const [isComparingToGoals, setIsComparingToGoals] = useState(false)
  const [platform, setPlatform] = useState<string | null>(searchParams.get(`platform`))
  const [showDisqualified, setShowDisqualified] = useState(searchParams.get(`showDisqualified`) === `true`)
  const [, summary, rawLocationRows, compareTo, isLocationsLoading] = useLocationMetrics(
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    locationIds,
    channel,
    channelOp,
    locationSearchQuery,
    integrationStatus,
    filters,
    platform,
    showDisqualified,
  )

  const [locationsMap, setLocationsMap] = useState<Record<string, any>>({})
  useEffect(() => {
    gqlQuery(MARKETING_LOCATIONS_QUERY, {}).then(({ data }) => {
      const map: Record<string, any> = {}
      for (const loc of data?.locations?.hits ?? []) map[loc.id] = loc
      setLocationsMap(map)
    })
  }, [])

  const locationRows = useMemo(() => {
    if (!rawLocationRows) return undefined
    return rawLocationRows.map((row) => {
      const loc = locationsMap[row.locationId]
      return loc ? { ...row, location: loc } : row
    })
  }, [rawLocationRows, locationsMap])
  const [staffRows, isStaffLoading] = useStaffMetrics(
    fromDate,
    toDate,
    locationIds,
    channel,
    staffSearchQuery,
    integrationStatus,
    filters,
    platform,
    false,
  )

  const [scorecardStaffRowsRaw, setScorecardStaffRowsRaw] = useState<StaffRow[]>()
  const [isScorecardStaffLoading, setIsScorecardStaffLoading] = useState(false)

  const scorecardStaffRows = useMemo(() => {
    if (!scorecardStaffRowsRaw) return undefined
    if (!staffSearchQuery) return scorecardStaffRowsRaw
    const q = staffSearchQuery.toLowerCase()
    return scorecardStaffRowsRaw.filter((row) => row.bucket.toLowerCase().includes(q))
  }, [scorecardStaffRowsRaw, staffSearchQuery])

  useEffect(() => {
    if (!fromDate || !toDate) return
    setIsScorecardStaffLoading(true)
    bg(() => {
      return gqlQuery(STAFF_BREAKDOWN_QUERY, {
        dateRange: { fromDate, toDate },
        locationIds: locationIds.length ? locationIds : undefined,
        channel: channel || undefined,
        integrationStatus: integrationStatus || undefined,
        filters: filters || undefined,
      }).then((result) => {
        setScorecardStaffRowsRaw(result.data?.staffBreakdown?.hits ?? [])
        setIsScorecardStaffLoading(false)
      })
    })
  }, [fromDate, toDate, locationIds, channel, integrationStatus, filters])

  // Deep link: open CallsModal when ?staff=X&callType=Y are in the URL
  useEffect(() => {
    const staffParam = searchParams.get(`staff`)
    const staffIdParam = searchParams.get(`staffId`)
    const callType = searchParams.get(`callType`)
    if (!staffParam || !callType || !fromDate || !toDate) return
    // Clear params immediately so the effect doesn't re-fire on reload
    setSearchParams((prev) => {
      prev.delete(`staff`)
      prev.delete(`staffId`)
      prev.delete(`callType`)
      return prev
    })
    openCallsModal({
      staff: staffParam,
      staffId: staffIdParam,
      callType,
      fromDate,
      toDate,
      channel,
      locationIds,
      filters,
    })
  }, [fromDate, toDate])

  const showOrgAdminColumns = checkPermission([`org:marketing:see-spend`, `marketing:spend`])

  const locationColumns = useMemo(
    () => defaultLocationColumns(showOrgAdminColumns, isSMCOrg(user?.orgId), location.search, canViewMarketingSpend),
    [user, showOrgAdminColumns, canViewMarketingSpend],
  )

  const defaultFilters: PredefinedFilter = {
    ...(searchParams.has(`platform`) && searchParams.get(`platform`) !== ``
      ? {
          [`platform`]: {
            operator: `exactly` as FilterOperator,
            value: searchParams.get(`platform`),
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`dashboardFilters`) && searchParams.get(`dashboardFilters`) !== ``
      ? decodeFilters(searchParams.get(`dashboardFilters`)!).reduce(
          (acc, filter) => ({
            ...acc,
            [filter.attribute]: {
              operator: filter.operator as FilterOperator,
              value: filter.value,
              readOnly: true,
            },
          }),
          {},
        )
      : undefined),
    ...(searchParams.has(`filters`) ? undefined : defaultNonPatientFilter),
    ...(searchParams.has(`channel`)
      ? {
          [`channel.keyword`]: {
            operator: `in` as FilterOperator,
            value: [searchParams.get(`channel`)!],
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`locationIds`)
      ? {
          [`locationId`]: {
            operator: `in` as FilterOperator,
            value: compact(decodeURIComponent(searchParams.get(`locationIds`)!).split(`,`)),
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`fromDate`) && searchParams.has(`toDate`)
      ? {
          [`becameLeadDate`]: {
            operator: `between` as FilterOperator,
            value: [searchParams.get(`fromDate`)!, searchParams.get(`toDate`)!],
            readOnly: true,
          },
        }
      : defaultBecameLeadDateFilter),
  }
  debug(`marketing`, `default filters`, defaultFilters)

  const staffColumns = useMemo(
    () =>
      getScorecardStaffColumns(
        fromDate,
        toDate,
        channel,
        defaultFilters,
        (staff, staffId, callType) => {
          openCallsModal({
            staff,
            staffId,
            callType,
            fromDate,
            toDate,
            channel,
            locationIds,
            filters,
          })
        },
        { channel, locationIds, filters },
      ),
    [fromDate, toDate, channel, locationIds, filters],
  )

  const [dontCompare, setDontCompare] = useState(false)

  useEffect(() => {
    setChannel(searchParams.get(`channel`) ?? ``)
    setPlatform(searchParams.get(`platform`) ?? ``)
  }, [searchParams])

  const dateChangeHandler = (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => {
    setFromDate(fromDate)
    setToDate(toDate)
    setCompareFromDate(compareFromDate)
    setCompareToDate(compareToDate)
    setIsComparingToGoals(comparingGoals)
    setDontCompare(dontCompare)
  }

  const handleTabChange = (tab: string) => {
    setOpenTab(tab)
    setSearchParams((prev) => {
      prev.set(`tab`, tab)
      return prev
    })
  }

  const handleShowDisqualifiedToggle = (value: boolean) => {
    setShowDisqualified(value)
    setSearchParams((prev) => {
      prev.set(`showDisqualified`, `${value}`)
      return prev
    })
  }

  return {
    user,
    channel,
    filters,
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    isComparingToGoals,
    dontCompare,
    platform,
    showDisqualified,
    openTab,
    canViewStaffBreakdown,
    canViewMarketingSpend,
    summary,
    compareTo,
    locationRows,
    isLocationsLoading,
    staffRows,
    isStaffLoading,
    scorecardStaffRows,
    isScorecardStaffLoading,
    locationColumns,
    staffColumns,
    locationIds,
    integrationStatus,
    defaultFilters,
    dateChangeHandler,
    handleTabChange,
    handleShowDisqualifiedToggle,
    setFilters,
    setLocationSearchQuery,
    setStaffSearchQuery,
  }
}
